import { Controller } from '@hotwired/stimulus';

/**
 * Two responsibilities:
 *
 * 1. Mobile menu open/close (toggle the .is-open class + sync aria-expanded).
 * 2. Scroll-aware "headroom" behaviour: hide the header on scroll-down,
 *    reveal it on scroll-up, and shrink the mascot to a small bar-fitting
 *    avatar as soon as the user scrolls past the page top.
 *
 *    - .is-scrolled on gm-nav      → mascot shrinks (balloon fades out,
 *                                     kindl figure crops to upper body)
 *    - .is-hidden  on <header>     → header slides up out of view
 *
 * Pure progressive enhancement — at scroll=0 (and with no JS) the bar
 * stays in its Figma full-size state.
 */
export default class extends Controller {
    connect() {
        this.lastScrollY = window.scrollY;
        this.ticking = false;
        this.header = this.element.closest('header');

        this.onScroll = this.onScroll.bind(this);
        this.scrollHandler = () => {
            if (!this.ticking) {
                window.requestAnimationFrame(this.onScroll);
                this.ticking = true;
            }
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        this.onScroll(); // sync initial state in case the page loaded already scrolled

        this.setupAccordion();
    }

    /**
     * Mobile accordion: each top-level item that has a submenu gets a
     * chevron toggle button. The label link stays navigable; tapping the
     * chevron expands/collapses that section (.is-expanded on the <li>).
     * The button is hidden on desktop via CSS, where the hover dropdown
     * takes over, so it's a harmless no-op there.
     */
    setupAccordion() {
        const items = this.element.querySelectorAll('.gm-nav__menu > li');
        items.forEach((li) => {
            const submenu = li.querySelector(':scope > ul');
            if (!submenu) return;

            const link = li.querySelector(':scope > a');
            const label = link ? link.textContent.trim() : 'Untermenü';

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'gm-nav__submenu-toggle';
            button.setAttribute('aria-expanded', 'false');
            button.setAttribute('aria-label', `${label} – Untermenü öffnen`);

            button.addEventListener('click', (event) => {
                event.preventDefault();
                const willOpen = !li.classList.contains('is-expanded');
                // Single-open accordion: collapse any other expanded section.
                if (willOpen) {
                    this.collapseAll();
                }
                li.classList.toggle('is-expanded', willOpen);
                button.setAttribute('aria-expanded', String(willOpen));
            });

            li.insertBefore(button, submenu);
        });
    }

    collapseAll() {
        this.element
            .querySelectorAll('.gm-nav__menu > li.is-expanded')
            .forEach((li) => {
                li.classList.remove('is-expanded');
                const button = li.querySelector(':scope > .gm-nav__submenu-toggle');
                if (button) {
                    button.setAttribute('aria-expanded', 'false');
                }
            });
    }

    disconnect() {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }

    onScroll() {
        const currentY = window.scrollY;
        const delta = currentY - this.lastScrollY;
        const isAtTop = currentY < 50;

        // Compact bar when not at the top.
        this.element.classList.toggle('is-scrolled', !isAtTop);

        // Hide on scroll-down past the first viewport; reveal on scroll-up
        // or whenever back at the top. Small delta threshold avoids
        // jitter from sub-pixel scroll events.
        if (this.header) {
            if (currentY > 200 && delta > 5) {
                this.header.classList.add('is-hidden');
            } else if (delta < -5 || isAtTop) {
                this.header.classList.remove('is-hidden');
            }
        }

        this.lastScrollY = currentY;
        this.ticking = false;
    }

    toggle(event) {
        event?.preventDefault();
        const isOpen = this.element.classList.toggle('is-open');
        const button = this.element.querySelector('.gm-nav__toggle');
        if (button) {
            button.setAttribute('aria-expanded', String(isOpen));
        }
        if (!isOpen) {
            this.collapseAll();
        }
    }

    close() {
        this.element.classList.remove('is-open');
        const button = this.element.querySelector('.gm-nav__toggle');
        if (button) {
            button.setAttribute('aria-expanded', 'false');
        }
        this.collapseAll();
    }
}
